import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { diaIso } from '@/comuns/formato';
import { quantidade, unidadeLabel } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { Fornecedor, Produto } from '@/data/tipos';

export interface MovimentacaoModalProps {
    alvo: { produto: Produto; tipo: 'entrada' | 'saida' } | null;
    fornecedores: Fornecedor[];
    onClose: () => void;
    onRegistrada: () => void;
}

const DESTINOS = [
    { value: 'Atendimento', label: 'Atendimento' },
    { value: 'Perda / descarte', label: 'Perda / descarte' },
    { value: 'Devolução ao fornecedor', label: 'Devolução ao fornecedor' },
    { value: 'Ajuste de inventário', label: 'Ajuste de inventário' },
];

export function MovimentacaoModal({ alvo, fornecedores, onClose, onRegistrada }: MovimentacaoModalProps) {
    const aviso = useAviso();
    const idQuantidade = useId();
    const idData = useId();
    const idOrigem = useId();
    const [valor, setValor] = useState('1');
    const [dia, setDia] = useState(diaIso());
    const [origem, setOrigem] = useState('');
    const formSujo = useAlteracaoPendente({ valor, dia, origem }, alvo && { alvo, fornecedores: fornecedores.length });
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const entrada = alvo?.tipo === 'entrada';

    useEffect(() => {
        if (!alvo) return;
        setValor('1');
        setDia(diaIso());
        setOrigem(alvo.tipo === 'entrada' ? (fornecedores.find((f) => f.ativo)?.nome ?? 'Entrada manual') : 'Atendimento');
        setErro(null);
    }, [alvo, fornecedores]);

    if (!alvo) return null;

    const produto = alvo.produto;
    const registrar = async () => {
        setSalvando(true);
        try {
            await dados().registrarMovimentacao({
                produtoId: produto.id,
                tipo: alvo.tipo,
                quantidade: Number(valor.replace(',', '.')) || 0,
                origem: origem.trim() || (entrada ? 'Entrada manual' : 'Saída manual'),
                ocorridoEm: dia,
            });
            aviso({
                title: entrada ? 'Entrada registrada' : 'Saída registrada',
                description: `${produto.nome} · ${quantidade(Number(valor.replace(',', '.')) || 0)} ${unidadeLabel(Number(valor.replace(',', '.')) || 0, produto.unidade)}`,
            });
            onRegistrada();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível registrar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={480}
            title={entrada ? 'Registrar entrada' : 'Registrar saída'}
            description={produto.nome}
            dirty={formSujo}
            onSave={() => void registrar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void registrar()} loading={salvando}>Registrar</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Quantidade" required htmlFor={idQuantidade} hint={`Em ${unidadeLabel(2, produto.unidade)}`}>
                        <Input id={idQuantidade} value={valor} onChange={(v) => { setValor(v); setErro(null); }} invalid={!!erro} />
                    </FormField>
                    <FormField label="Data" htmlFor={idData}>
                        <Input id={idData} type="date" value={dia} onChange={setDia} />
                    </FormField>
                    <FormField
                        label={entrada ? 'Fornecedor' : 'Vinculado a'} htmlFor={idOrigem} style={{ gridColumn: '1 / -1' }}
                        hint="Fica gravado na movimentação — é o que responde para onde foi"
                    >
                        <Select
                            id={idOrigem} value={origem} onChange={setOrigem}
                            options={entrada
                                ? fornecedores.map((f) => ({ value: f.nome, label: f.nome })).concat({ value: 'Entrada manual', label: 'Entrada manual' })
                                : DESTINOS}
                        />
                    </FormField>
                </div>

                <Alert tone="neutral" title="Estoque agora" dismissible={false}>
                    {quantidade(produto.quantidade)} {unidadeLabel(produto.quantidade, produto.unidade)} em estoque
                    {produto.reservado > 0 ? ` · ${quantidade(produto.reservado)} reservados` : ''}
                    {' '}· mínimo {quantidade(produto.minimo)}
                </Alert>
            </div>
        </Modal>
    );
}
